/**
 * WO EINE MASCHINENZEICHENKETTE UMBRECHEN DARF.
 *
 * ORIGINALTEXT MARVIN (06.09.2026, mit einem Bild von „Auftrag vorbereiten")
 * > „darstellungsfehler rechts bei den blöcken der gemeinde"
 *
 * Auf seinem Bild steht in der Karte „Gemeinde, gegengeprüft" die Zeile
 * `aufgrabungsmanagement@stadt.koble` - abgeschnitten am rechten Rand -, und
 * darunter „0 Werktage aus der globalen Vorgabe (" ebenso.
 *
 * ## DER GRUND IST EIN WORT OHNE BRUCHSTELLE
 *
 * `aufgrabungsmanagement@stadt.koblenz.de` sind 38 Zeichen **ohne ein einziges
 * Leerzeichen und ohne Bindestrich**. Ein Browser bricht von sich aus an einem
 * Leerzeichen und nach einem Bindestrich; am Klammeraffen und am Punkt bricht
 * er NICHT. Die Zeichenkette ist damit ein einziges Wort, das breiter ist als
 * die Spalte - und in einer Tabelle bestimmt das breiteste Wort die Breite der
 * SPALTE. Deshalb war auch der Satz darunter abgeschnitten, obwohl der ganz
 * normale Leerzeichen trägt: **eine einzige zu breite Zelle zieht die Spalte
 * auf, und alles andere in ihr ragt mit hinaus.**
 *
 * Nachgemessen am 06.09.2026: dieselbe Karte mit
 * `ordnungsverwaltung@bad-hoenningen-vg.de` (39 Zeichen, ALSO LÄNGER) bricht
 * sauber um - dort stehen Bindestriche. **Die Länge war nie das Problem.**
 *
 * ## WARUM NICHT EINFACH `overflow-wrap: anywhere`
 *
 * Weil dieses Haus die Antwort dreimal bezahlt hat (25.08., 26.08. und noch
 * einmal im Aufmaß): `anywhere` erlaubt dem Umbruchsatz, eine Zelle auf einen
 * Buchstaben zu quetschen, und dann steht dort „Bearbeitu/ngszeit" oder
 * „Tiefbau / arbeite / n". Eine Regel, die JEDES Wort brechen darf, bricht
 * irgendwann das falsche.
 *
 * Gebrochen wird deshalb dort, wo eine Adresse ohnehin gelesen wird: **nach
 * dem Klammeraffen und nach einem Punkt, einem Schrägstrich, einem
 * Bindestrich und einem Unterstrich.** Nicht davor - ein Punkt am Anfang einer
 * Zeile sieht aus wie ein Tippfehler.
 *
 * DIE STÜCKE TRAGEN IHR TRENNZEICHEN MIT. Zwischen ihnen steht im Bild ein
 * `<wbr>`, und das ist ein Element ohne Inhalt: **wer die Adresse markiert und
 * kopiert, bekommt sie Zeichen für Zeichen zurück** - genau das, was ein
 * eingefügtes Zeichen (etwa ein schmales Leerzeichen) kaputtmachen würde.
 *
 * DIESE DATEI TRÄGT KEIN JSX, damit ein Prüfstand sie WIRKLICH ausrechnen
 * kann. Elfte Datei dieser Art nach `namen.ts`, `terminraster.ts`,
 * `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts`, `kartenebene.ts`,
 * `grenzpunkte.ts`, `handbuchmarken.ts`, `tabellenbeschriftung.ts` und
 * `zahlwoerter.ts`.
 */

/** Nach diesen Zeichen darf umgebrochen werden. */
export const NACH = '@./-_';

/**
 * Eine Zeichenkette in die Stücke zerlegen, zwischen denen umgebrochen werden
 * darf. Jedes Stück endet auf seinem Trennzeichen; zusammengesetzt ergeben die
 * Stücke wieder den ursprünglichen Text.
 *
 * Zwei Trennzeichen hintereinander ergeben KEIN leeres Stück: eine Bruchstelle
 * mitten in „..", und die Zeile danach fängt mit einem Punkt an.
 */
export function bruchstuecke(text: string): string[] {
  const t = text ?? '';
  if (!t) return [];
  const raus: string[] = [];
  let stueck = '';
  for (let i = 0; i < t.length; i++) {
    stueck += t[i];
    if (!NACH.includes(t[i])) continue;
    /* Nicht brechen, solange das nächste Zeichen wieder ein Trennzeichen ist:
       sonst steht am Anfang der nächsten Zeile ein einzelner Punkt. */
    if (i + 1 < t.length && NACH.includes(t[i + 1])) continue;
    /* Und nicht am Ende: ein Bruch hinter dem letzten Zeichen bringt nichts. */
    if (i + 1 >= t.length) continue;
    raus.push(stueck);
    stueck = '';
  }
  if (stueck) raus.push(stueck);
  return raus;
}

/**
 * LOHNT SICH DER AUFWAND ÜBERHAUPT?
 *
 * Ein Text mit Leerzeichen bricht von selbst um; dort wären zusätzliche
 * Bruchstellen nur eine Gelegenheit, an einer hässlichen Stelle zu brechen.
 * Gebrochen wird deshalb nur, was ein WORT ist und lang genug, um eine schmale
 * Spalte zu sprengen.
 */
export function brauchtBruch(text: string, abZeichen = 18): boolean {
  const t = (text ?? '').trim();
  if (t.length < abZeichen) return false;
  if (/\s/.test(t)) return false;
  return bruchstuecke(t).length > 1;
}
